import React from 'react';
import '../styles/About.css';
import '../styles/Skills.css';
import RecentPosts from './RecentPosts';

// Hero Composition Assets (New Transparent Torn-Paper Avatar & UX Flowchart)
import portraitCircle from '../images/about-avatar-new-transparent.png';
import shadowGlow1 from '../images/home/Vector.svg';
import shadowGlow2 from '../images/home/Vector-1.svg';
import dotGrid from '../images/home/Dot Grid.svg';
import flowchartVector from '../images/about-ux-flowchart-vector.svg';

// Expertise Isometric Illustrations (Transparent & Padded PNG)
import aboutEnterprisePlatformImg from '../images/my-skills/about-enterprise-platform.png';
import aboutDesignSystemsImg from '../images/my-skills/about-design-systems.png';
import aboutAmbiguityClarityImg from '../images/my-skills/about-ambiguity-clarity.png';
import aboutDesignEngineeringImg from '../images/my-skills/about-design-engineering.png';

function About() {
    // Interactive states for Expertise section
    const [hoveredCursor, setHoveredCursor] = React.useState(null);
    const [copyToast, setCopyToast] = React.useState(null);
    const [activeThemeMode, setActiveThemeMode] = React.useState('light');
    const [codeVariant, setCodeVariant] = React.useState('primary');
    const [activeInsight, setActiveInsight] = React.useState(null);

    const handleCopyToken = (hex) => {
        setCopyToast(`Copied ${hex}! ⚡`);
        setTimeout(() => setCopyToast(null), 2000);
    };

    const toggleCodeVariant = () => {
        setCodeVariant(prev => prev === 'primary' ? 'success' : 'primary');
    };

    return (
        <>
        {/* About Hero Section */}
        <section id="about" className="about-section position-relative">
            <div className="container">
                <div className="row align-items-center">
                    <div className="col-lg-6 pe-lg-5 order-2 order-lg-1 mt-4 mt-lg-0">
                        <h1 className="about-hero-title">Turning Complexity into Usable Systems</h1>
                        <div className="mt-4 pe-lg-4">
                            <p className="about-hero-lead">
                                I’m a Senior Product Designer and UX Engineer with experience designing and shipping <b>enterprise software</b> across <b>identity management, transportation, healthcare, ecommerce, and financial systems</b>.
                            </p>
                            <p className="about-hero-text">
                                My work often sits at the intersection of users, business priorities, and technical complexity. I specialize in bringing structure to ambiguous challenges, aligning stakeholders, and creating scalable experiences that teams can build with confidence.
                            </p>
                            <p className="about-hero-text">
                                Because I understand both design and front-end implementation, I help reduce friction between concept and execution, leading to faster delivery and stronger outcomes.
                            </p>
                        </div>
                    </div>
                    <div className="col-lg-6 text-center about-hero-container order-1 order-lg-2 mb-4 mb-lg-0 position-relative">
                        {/* Background Dot Grid & Glows */}
                        <img src={dotGrid} alt="" className="about-hero-layer about-dot-grid" />
                        <img src={shadowGlow1} alt="" className="about-hero-layer about-shadow-1" />
                        <img src={shadowGlow2} alt="" className="about-hero-layer about-shadow-2" />

                        {/* Flowchart Vector Connecting Paths */}
                        <img src={flowchartVector} alt="" className="about-hero-layer about-flowchart-lines" />

                        {/* Centered Transparent Torn-Paper Avatar */}
                        <div className="about-portrait-wrapper position-relative d-inline-block">
                            <img src={portraitCircle} alt="Michelle Maltbia" className="about-portrait-image" />
                        </div>

                        {/* 1. Top-Left Outer: Ambiguous Requirements */}
                        <div className="about-hero-layer about-ux-card node-top-left">
                            <div className="node-header mb-1">
                                <span className="node-icon">❓</span>
                                <span className="node-title">Ambiguous Requirements</span>
                            </div>
                            <div className="wireframe-skeleton">
                                <div className="skeleton-bar bar-dashed bar-wide"></div>
                                <div className="skeleton-bar bar-dashed bar-narrow"></div>
                            </div>
                        </div>

                        {/* 2. Top-Right Outer: Shipped Enterprise System */}
                        <div className="about-hero-layer about-ux-card node-top-right">
                            <div className="d-flex align-items-center gap-2">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <polyline points="22 4 12 14.01 9 11.01" />
                                </svg>
                                <div className="text-start">
                                    <span className="node-title-bold d-block">Shipped Enterprise System</span>
                                    <span className="node-subtext text-success">Identity &amp; Enterprise Scale</span>
                                </div>
                            </div>
                        </div>

                        {/* 3. Bottom-Left Outer: Cross-Functional Alignment */}
                        <div className="about-hero-layer about-chip-dark node-bottom-left">
                            <span className="node-icon me-1">🤝</span>
                            <span className="chip-text">Cross-Functional Alignment</span>
                        </div>

                        {/* 4. Bottom-Right Outer: Enterprise Design System & React */}
                        <div className="about-hero-layer about-ux-card node-bottom-right">
                            <div className="node-header mb-1">
                                <span className="node-icon">🎨</span>
                                <span className="node-title">Enterprise Design System</span>
                            </div>
                            <div className="d-flex gap-1 align-items-center mt-1">
                                <span className="token-pill pill-coral">#E76A60</span>
                                <span className="token-pill pill-blue">#5D81A9</span>
                                <span className="token-pill pill-green">React Code</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* Expertise Section */}
        <section className="about-expertise-section pb-5 pt-0">
            <div className="container">
                {/* Section Heading */}
                <h2 className="skills-title text-start mb-0">Expertise</h2>

                {/* Card 1: Designing Enterprise Platforms */}
                <div className="row mt-0 mb-5 pt-0 pb-4 align-items-center">
                    <div className="col-lg-6 order-2 order-lg-1 text-start pe-lg-4">
                        <div className="expertise-text-card skills-item">
                            <span className="transformation-badge mb-2">
                                Complex System Requirements → Scalable Platform Architecture
                            </span>
                            <h3 className="skills-heading mb-3">Designing Enterprise Platforms</h3>
                            <p className="skills-desc mb-3">
                                Architecting software across identity management, rail freight telemetry, and multi-brand e-commerce—structuring complex user permissions, roles, audit logs, and data relationships into intuitive, high-performance systems.
                            </p>
                            <div className="expertise-tags-container">
                                <span className="expertise-tag">System Maps</span>
                                <span className="expertise-tag">Permissions &amp; Roles</span>
                                <span className="expertise-tag">Data Relationships</span>
                                <span className="expertise-tag">Multi-Tenant Architecture</span>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6 order-1 order-lg-2 text-center mb-4 mb-lg-0">
                        <div className="expertise-card-visual visual-platform">
                            <img
                                src={aboutEnterprisePlatformImg}
                                alt="Designing Enterprise Platforms 3D Isometric Visual"
                                className="img-fluid expertise-iso-img"
                            />
                            <div className="expertise-interactive-overlay">
                                {/* Cursor Badge */}
                                <div 
                                    className="interactive-cursor-badge cursor-michelle"
                                    onMouseEnter={() => setHoveredCursor('michelle')}
                                    onMouseLeave={() => setHoveredCursor(null)}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M 0 0 L 24 9 L 14 14 L 9 24 Z" />
                                    </svg>
                                    Michelle (Lead UX)
                                    {hoveredCursor === 'michelle' && (
                                        <div className="cursor-comment-popover">
                                            RBAC permissions &amp; identity SSO mapped for multi-tenant platform ⚡
                                        </div>
                                    )}
                                </div>
                                {/* Interactive Action Bar */}
                                <div className="interactive-action-bar">
                                    <button className="interactive-pill-btn active">Live Stream: 99.9% Uptime ⚡</button>
                                    <button className="interactive-pill-btn pill-green">Okta SSO ✓</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card 2: Building Scalable Design Systems */}
                <div className="row mb-5 py-4 align-items-center">
                    <div className="col-lg-6 text-center mb-4 mb-lg-0">
                        <div className="expertise-card-visual visual-design-system">
                            <img
                                src={aboutDesignSystemsImg}
                                alt="Building Scalable Design Systems 3D Isometric Visual"
                                className="img-fluid expertise-iso-img"
                            />
                            {copyToast && <div className="copy-toast-notification">{copyToast}</div>}
                            <div className="expertise-interactive-overlay">
                                {/* Cursor Badge */}
                                <div 
                                    className="interactive-cursor-badge cursor-david"
                                    onMouseEnter={() => setHoveredCursor('david')}
                                    onMouseLeave={() => setHoveredCursor(null)}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M 0 0 L 24 9 L 14 14 L 9 24 Z" />
                                    </svg>
                                    David (Design Systems)
                                    {hoveredCursor === 'david' && (
                                        <div className="cursor-comment-popover">
                                            Tokens synced with Figma &amp; Storybook 🎨
                                        </div>
                                    )}
                                </div>
                                {/* Interactive Action Bar */}
                                <div className="interactive-action-bar">
                                    <button className="interactive-pill-btn" onClick={() => handleCopyToken('#2563EB')}>
                                        Copy #2563EB
                                    </button>
                                    <button className="interactive-pill-btn" onClick={() => handleCopyToken('#059669')}>
                                        Copy #059669
                                    </button>
                                    <button className={`interactive-pill-btn ${activeThemeMode === 'light' ? 'active' : ''}`} onClick={() => setActiveThemeMode(activeThemeMode === 'light' ? 'dark' : 'light')}>
                                        {activeThemeMode === 'light' ? '☀️ Light Mode' : '🌙 Dark Mode'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6 text-start ps-lg-4">
                        <div className="expertise-text-card skills-item">
                            <span className="transformation-badge mb-2">
                                Components &amp; Variables → Unified System Scale
                            </span>
                            <h3 className="skills-heading mb-3">Building Scalable Design Systems</h3>
                            <p className="skills-desc mb-3">
                                Building multi-brand token frameworks and component libraries that scale. Establishing design tokens, spacing systems, WCAG AA accessibility guidelines, and component APIs that unify design and engineering teams.
                            </p>
                            <div className="expertise-tags-container">
                                <span className="expertise-tag">Token Architecture</span>
                                <span className="expertise-tag">Component APIs</span>
                                <span className="expertise-tag">Accessibility (WCAG AA)</span>
                                <span className="expertise-tag">Storybook</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card 3: Making Complex Problems Actionable */}
                <div className="row mb-5 py-4 align-items-center">
                    <div className="col-lg-6 order-2 order-lg-1 text-start pe-lg-4">
                        <div className="expertise-text-card skills-item">
                            <span className="transformation-badge mb-2">
                                Ambiguous Input → Prioritized Product Strategy
                            </span>
                            <h3 className="skills-heading mb-3">Making Complex Problems Actionable</h3>
                            <p className="skills-desc mb-3">
                                Synthesizing fragmented stakeholder inputs, legacy system constraints, Slack threads, and customer feedback into clear information architecture, user flows, and prioritized engineering roadmaps.
                            </p>
                            <div className="expertise-tags-container">
                                <span className="expertise-tag">Information Architecture</span>
                                <span className="expertise-tag">User Flow Mapping</span>
                                <span className="expertise-tag">Stakeholder Alignment</span>
                                <span className="expertise-tag">Product Strategy</span>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6 order-1 order-lg-2 text-center mb-4 mb-lg-0">
                        <div className="expertise-card-visual visual-ambiguity">
                            <img
                                src={aboutAmbiguityClarityImg}
                                alt="Making Complex Problems Actionable 3D Isometric Visual"
                                className="img-fluid expertise-iso-img"
                            />
                            <div className="expertise-interactive-overlay">
                                {/* Cursor Badge */}
                                <div 
                                    className="interactive-cursor-badge cursor-alex"
                                    onMouseEnter={() => setHoveredCursor('alex')}
                                    onMouseLeave={() => setHoveredCursor(null)}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M 0 0 L 24 9 L 14 14 L 9 24 Z" />
                                    </svg>
                                    Alex (Product Manager)
                                    {hoveredCursor === 'alex' && (
                                        <div className="cursor-comment-popover">
                                            Roadmap aligned for Sprint 14 🚀
                                        </div>
                                    )}
                                </div>
                                {/* Interactive Action Bar */}
                                <div className="interactive-action-bar">
                                    <button 
                                        className={`interactive-pill-btn ${activeInsight === 'ia' ? 'active' : ''}`}
                                        onClick={() => setActiveInsight(activeInsight === 'ia' ? null : 'ia')}
                                    >
                                        {activeInsight === 'ia' ? '✓ 40+ User Interviews Synthesized' : '📌 Explore User Flows & IA'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card 4: Bridging Design & Engineering */}
                <div className="row mb-5 py-4 align-items-center">
                    <div className="col-lg-6 text-center mb-4 mb-lg-0">
                        <div className="expertise-card-visual visual-code">
                            <img
                                src={aboutDesignEngineeringImg}
                                alt="Bridging Design & Engineering 3D Isometric Visual"
                                className="img-fluid expertise-iso-img"
                            />
                            <div className="expertise-interactive-overlay">
                                {/* Cursor Badge */}
                                <div 
                                    className="interactive-cursor-badge cursor-sarah"
                                    onMouseEnter={() => setHoveredCursor('sarah')}
                                    onMouseLeave={() => setHoveredCursor(null)}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M 0 0 L 24 9 L 14 14 L 9 24 Z" />
                                    </svg>
                                    Sarah (Lead Engineer)
                                    {hoveredCursor === 'sarah' && (
                                        <div className="cursor-comment-popover">
                                            100% layout fidelity with React &amp; CSS variables 💻
                                        </div>
                                    )}
                                </div>
                                {/* Interactive Action Bar */}
                                <div className="interactive-action-bar">
                                    <button className="interactive-pill-btn pill-green" onClick={toggleCodeVariant}>
                                        variant="{codeVariant}" ⚡
                                    </button>
                                    <button className="interactive-pill-btn active">✓ Merge Success</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6 text-start ps-lg-4">
                        <div className="expertise-text-card skills-item">
                            <span className="transformation-badge mb-2">
                                Figma Specs → Production React Code
                            </span>
                            <h3 className="skills-heading mb-3">Bridging Design &amp; Engineering</h3>
                            <p className="skills-desc mb-3">
                                Bridging the gap between design concepts and production code. Writing clean, modular React components, establishing CSS custom properties, defining type-safe component APIs, and ensuring 100% layout fidelity.
                            </p>
                            <div className="expertise-tags-container">
                                <span className="expertise-tag">React / TypeScript</span>
                                <span className="expertise-tag">CSS Custom Properties</span>
                                <span className="expertise-tag">Component APIs</span>
                                <span className="expertise-tag">Layout Parity</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <RecentPosts />
        </>
    );
}

export default About;
